import React, { useState, useEffect, useCallback } from 'react';
import { get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppSettings } from '../contexts/AppSettingsContext';
import useSimulatorAPI from '../hooks/useSimulatorAPI';
import './Menu.css';

// Skeleton Components
const CategorySkeleton = ({ delay = 0 }) => (
  <div className="category-item skeleton-item" style={{ animationDelay: `${delay}ms` }}>
    <div className="category-content">
      <div className="skeleton skeleton-text" style={{ width: '70%', animationDelay: `${delay}ms` }}></div>
      <div className="skeleton skeleton-text-small" style={{ width: '30px', animationDelay: `${delay + 50}ms` }}></div>
    </div>
  </div>
);

const ItemCardSkeleton = ({ delay = 0 }) => (
  <div className="item-card skeleton-card">
    <div className="skeleton skeleton-image" style={{ animationDelay: `${delay}ms` }}></div>
    <div className="item-content">
      <div className="item-header">
        <div className="skeleton skeleton-title" style={{ width: '60%', animationDelay: `${delay + 100}ms` }}></div>
        <div className="skeleton skeleton-price" style={{ animationDelay: `${delay + 150}ms` }}></div>
      </div>
      <div className="skeleton skeleton-text" style={{ width: '100%', marginBottom: '8px', animationDelay: `${delay + 200}ms` }}></div>
      <div className="skeleton skeleton-text" style={{ width: '85%', marginBottom: '8px', animationDelay: `${delay + 250}ms` }}></div>
      <div className="skeleton skeleton-text" style={{ width: '40%', animationDelay: `${delay + 300}ms` }}></div>
      <div className="item-dietary" style={{ marginTop: '12px' }}>
        <div className="skeleton skeleton-badge" style={{ animationDelay: `${delay + 350}ms` }}></div>
        <div className="skeleton skeleton-badge" style={{ animationDelay: `${delay + 400}ms` }}></div>
      </div>
    </div>
  </div>
);

const CategoriesSkeleton = ({ count = 5 }) => (
  <div className="categories-list">
    {Array.from({ length: count }).map((_, i) => (
      <CategorySkeleton key={i} delay={i * 100} />
    ))}
  </div>
);

const ItemsGridSkeleton = ({ count = 6 }) => (
  <div className="items-grid">
    {Array.from({ length: count }).map((_, i) => (
      <ItemCardSkeleton key={i} delay={i * 75} />
    ))}
  </div>
);

const DIETARY_TAGS = [
  { id: 'V', label: 'Vegetarian', color: '#22c55e' },
  { id: 'GF', label: 'Gluten-Free', color: '#3b82f6' },
  { id: 'VG', label: 'Vegan', color: '#a855f7' },
  { id: 'DF', label: 'Dairy-Free', color: '#f97316' },
];

// Display order matches a Sunday-start calendar. Codes are the canonical wire format.
const HH_WEEKDAYS = [
  { code: 'sun', short: 'Sun', label: 'Sunday' },
  { code: 'mon', short: 'Mon', label: 'Monday' },
  { code: 'tue', short: 'Tue', label: 'Tuesday' },
  { code: 'wed', short: 'Wed', label: 'Wednesday' },
  { code: 'thu', short: 'Thu', label: 'Thursday' },
  { code: 'fri', short: 'Fri', label: 'Friday' },
  { code: 'sat', short: 'Sat', label: 'Saturday' },
];
const HH_WEEKDAY_ORDER = HH_WEEKDAYS.map(d => d.code);
const HH_PRESETS = [
  { id: 'weekdays', label: 'Weekdays', days: ['mon', 'tue', 'wed', 'thu', 'fri'] },
  { id: 'weekends', label: 'Weekends', days: ['sat', 'sun'] },
  { id: 'daily', label: 'Daily', days: ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] },
];

const sortDayCodes = (codes) => {
  const set = new Set(codes);
  return HH_WEEKDAY_ORDER.filter(c => set.has(c));
};

const formatPrice = (price) => {
  const numericValue = parseFloat(String(price).replace(/[^0-9.]/g, ''));
  if (isNaN(numericValue)) return '$0.00';
  return `$${numericValue.toFixed(2)}`;
};

const Menu = () => {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const { getAuthToken } = useAuth();
  const { dataSource, isSimulator, happyHourSchedule, savingHappyHour, setHappyHourSchedule } = useAppSettings();

  // Happy Hour schedule editor (writes to the `happy_hour_schedule` setting).
  const [hhDays, setHhDays] = useState([]);
  const [hhStart, setHhStart] = useState('');
  const [hhEnd, setHhEnd] = useState('');
  const [hhNote, setHhNote] = useState('');
  const [hhError, setHhError] = useState(null);
  const [hhSuccess, setHhSuccess] = useState(null);

  useEffect(() => {
    const incoming = Array.isArray(happyHourSchedule?.days) ? happyHourSchedule.days : [];
    // Filter to known codes; ignore legacy `days_label` (free-form string).
    setHhDays(sortDayCodes(incoming.filter(c => HH_WEEKDAY_ORDER.includes(c))));
    setHhStart(happyHourSchedule?.start || '');
    setHhEnd(happyHourSchedule?.end || '');
    setHhNote(happyHourSchedule?.note || '');
  }, [happyHourSchedule]);

  const toggleHhDay = (code) => {
    setHhDays(prev => {
      const set = new Set(prev);
      if (set.has(code)) set.delete(code); else set.add(code);
      return sortDayCodes(Array.from(set));
    });
  };

  const applyHhPreset = (days) => setHhDays(sortDayCodes(days));

  const handleSaveHappyHour = async () => {
    setHhError(null);
    setHhSuccess(null);
    const payload = {};
    if (hhDays.length > 0) payload.days = sortDayCodes(hhDays);
    if (hhStart.trim()) payload.start = hhStart.trim();
    if (hhEnd.trim()) payload.end = hhEnd.trim();
    if (hhNote.trim()) payload.note = hhNote.trim();
    try {
      // Send null to clear the schedule entirely when no fields are set.
      await setHappyHourSchedule(Object.keys(payload).length ? payload : null);
      setHhSuccess('Happy Hour schedule saved.');
    } catch (err) {
      console.error('Failed to save HH schedule:', err);
      setHhError('Failed to save Happy Hour schedule.');
    }
  };

  // Editor state
  const sim = useSimulatorAPI();
  const [rawItems, setRawItems] = useState([]);
  const [rawOptionGroups, setRawOptionGroups] = useState([]);
  const [rawPreModifiers, setRawPreModifiers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    fetchMenu();
  }, [dataSource]);

  const fetchMenu = async () => {
    try {
      setLoadingMenu(true);
      setError(null);
      const restOperation = get({ apiName: 'ChulasAdminAPI', path: '/v1/menu' });
      const response = await restOperation.response;
      const data = await response.body.json();
      const menuCategories = data.categories || [];
      setCategories(menuCategories);
      if (menuCategories.length > 0 && !activeCategory) {
        setActiveCategory(menuCategories[0]);
      } else if (activeCategory) {
        const updated = menuCategories.find(c => c.category_id === activeCategory.category_id);
        if (updated) setActiveCategory(updated);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError('Failed to fetch menu');
    } finally {
      setLoadingMenu(false);
    }
  };

  const fetchRawMenuData = useCallback(async () => {
    try {
      const [items, optGroups, preMods] = await Promise.all([
        sim.callSimAPI('/sim/menu/items'),
        sim.callSimAPI('/sim/menu/option-groups'),
        sim.callSimAPI('/sim/menu/pre-modifiers'),
      ]);
      setRawItems(items || []);
      setRawOptionGroups(optGroups || []);
      setRawPreModifiers(preMods || []);
    } catch (err) {
      console.error('Error fetching raw menu data:', err);
    }
  }, [sim]);

  useEffect(() => {
    if (isSimulator && !loadingMenu && categories.length > 0) {
      fetchRawMenuData();
    }
  }, [isSimulator, loadingMenu, categories.length]);

  const openEditModal = (item) => {
    if (!isSimulator) return;
    const raw = rawItems.find(r => r.guid === item.item_id);
    if (!raw) return;
    setEditingItem(raw);
    setEditForm({
      name: raw.name || '',
      description: raw.description || '',
      price: raw.price || 0,
      calories: raw.calories || '',
      ingredients: raw.ingredients || '',
      dietaryTags: raw.dietaryTags || [],
      featured: raw.featured || false,
      optionGroups: raw.optionGroups || [],
    });
    setSaveError(null);
    setShowEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await sim.callSimAPI(`/sim/menu/items/${editingItem.guid}`, {
        method: 'PUT',
        body: {
          name: editForm.name,
          description: editForm.description,
          price: parseFloat(editForm.price) || 0,
          calories: editForm.calories ? parseInt(editForm.calories) : null,
          ingredients: editForm.ingredients,
          dietaryTags: editForm.dietaryTags,
          featured: editForm.featured,
          optionGroups: editForm.optionGroups,
        },
      });
      setSuccessMessage('Item updated');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowEditModal(false);
      await Promise.all([fetchMenu(), fetchRawMenuData()]);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;
    setImageUploading(true);
    try {
      const result = await sim.callSimAPI('/sim/images', {
        method: 'POST',
        body: { items: [{ guid: editingItem.guid, filename: file.name }] },
      });
      const uploadInfo = result.results?.[0];
      if (!uploadInfo?.uploadUrl) throw new Error('No upload URL returned');
      await fetch(uploadInfo.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const updatedRaw = await sim.callSimAPI(`/sim/menu/items/${editingItem.guid}`);
      setEditingItem(updatedRaw);
      setSuccessMessage('Image uploaded');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setSaveError('Image upload failed: ' + err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const toggleDietaryTag = (tagId) => {
    setEditForm(prev => ({
      ...prev,
      dietaryTags: prev.dietaryTags.includes(tagId)
        ? prev.dietaryTags.filter(t => t !== tagId)
        : [...prev.dietaryTags, tagId],
    }));
  };

  const removeModifierGroup = (ogGuid) => {
    setEditForm(prev => ({
      ...prev,
      optionGroups: prev.optionGroups.filter(og => {
        const guid = typeof og === 'string' ? og : og.guid;
        return guid !== ogGuid;
      }),
    }));
  };

  const addModifierGroup = (ogGuid) => {
    const alreadyAttached = editForm.optionGroups.some(og => {
      const guid = typeof og === 'string' ? og : og.guid;
      return guid === ogGuid;
    });
    if (alreadyAttached) return;
    setEditForm(prev => ({
      ...prev,
      optionGroups: [...prev.optionGroups, { guid: ogGuid, entityType: 'MenuOptionGroup' }],
    }));
  };

  const getModifierGroupName = (ogRef) => {
    const guid = typeof ogRef === 'string' ? ogRef : ogRef.guid;
    const og = rawOptionGroups.find(g => g.guid === guid);
    return og?.name || guid.slice(0, 8);
  };

  const getModifierOptions = (ogRef) => {
    const guid = typeof ogRef === 'string' ? ogRef : ogRef.guid;
    const og = rawOptionGroups.find(g => g.guid === guid);
    if (!og) return [];
    return (og.options || []).map(optRef => {
      const optGuid = typeof optRef === 'string' ? optRef : optRef.guid;
      return rawPreModifiers.find(pm => pm.guid === optGuid);
    }).filter(Boolean);
  };

  const attachedGuids = new Set(
    editForm.optionGroups?.map(og => typeof og === 'string' ? og : og.guid) || []
  );
  const availableGroups = rawOptionGroups.filter(og => !attachedGuids.has(og.guid));

  return (
    <div className="menu-container">
      <div className="menu-header">
        <div>
          <h1>{isSimulator ? 'Menu Editor' : 'Menu Viewer'}</h1>
          <p className="page-description">
            {isSimulator
              ? 'Click any item to edit. Changes are saved to the simulator.'
              : 'Viewing live menu from Toast POS system.'}
          </p>
        </div>
        <span className={`toggle-status ${dataSource === 'toast' ? 'status-toast' : 'status-admin'}`}>
          {dataSource === 'toast' ? 'LIVE' : 'SIM'}
        </span>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Happy Hour schedule — drives the promoted card on the customer home screen.
          Leave any field blank to omit it. */}
      <div className="happy-hour-editor">
        <div className="happy-hour-editor-header">
          <h2>Happy Hour Schedule</h2>
          <p className="page-description">
            Shows up on the customer home screen above the menu. Leave fields blank to hide them.
          </p>
        </div>
        {hhError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{hhError}</div>}
        {hhSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{hhSuccess}</div>}
        <div className="happy-hour-days-field">
          <div className="happy-hour-days-row">
            <label>Days</label>
            <div className="happy-hour-presets">
              {HH_PRESETS.map(p => (
                <button
                  type="button"
                  key={p.id}
                  className="happy-hour-preset-btn"
                  onClick={() => applyHhPreset(p.days)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                className="happy-hour-preset-btn happy-hour-preset-btn--clear"
                onClick={() => setHhDays([])}
                disabled={hhDays.length === 0}
              >
                Clear
              </button>
            </div>
          </div>
          <div className="happy-hour-days-toggle-row" role="group" aria-label="Days of week">
            {HH_WEEKDAYS.map(d => {
              const active = hhDays.includes(d.code);
              return (
                <button
                  type="button"
                  key={d.code}
                  className={`happy-hour-day-toggle ${active ? 'active' : ''}`}
                  aria-pressed={active}
                  aria-label={d.label}
                  onClick={() => toggleHhDay(d.code)}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </div>
        <div className="happy-hour-editor-grid">
          <div className="edit-field">
            <label>Start time</label>
            <input
              type="time"
              value={hhStart}
              onChange={(e) => setHhStart(e.target.value)}
            />
          </div>
          <div className="edit-field">
            <label>End time</label>
            <input
              type="time"
              value={hhEnd}
              onChange={(e) => setHhEnd(e.target.value)}
            />
          </div>
          <div className="edit-field happy-hour-note-field">
            <label>Note (optional)</label>
            <input
              type="text"
              placeholder="e.g. Bar only"
              value={hhNote}
              onChange={(e) => setHhNote(e.target.value)}
            />
          </div>
        </div>
        <div className="happy-hour-editor-actions">
          <button
            className="btn btn-primary"
            onClick={handleSaveHappyHour}
            disabled={savingHappyHour}
          >
            {savingHappyHour ? 'Saving…' : 'Save Happy Hour'}
          </button>
        </div>
      </div>

      {dataSource === 'toast' && (
        <div className="alert alert-info">
          <strong>Toast Mode Active:</strong> Displaying menu from Toast POS.
        </div>
      )}

      <div className="menu-layout">
        {/* Sidebar */}
        <div className="menu-sidebar">
          <div className="sidebar-header"><h2>Categories</h2></div>
          {loadingMenu ? (
            <CategoriesSkeleton count={5} />
          ) : (
            <div className="categories-list">
              {categories.map((category) => (
                <div
                  key={category.category_id}
                  className={`category-item ${activeCategory?.category_id === category.category_id ? 'active' : ''}`}
                >
                  <div className="category-content" onClick={() => setActiveCategory(category)}>
                    <span className="category-name">{category.name}</span>
                    <span className="category-count">({category.items?.length || 0})</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="menu-main">
          {loadingMenu ? (
            <div className="items-section">
              <div className="section-header">
                <div className="skeleton skeleton-title" style={{ width: '150px', height: '28px' }}></div>
              </div>
              <ItemsGridSkeleton count={6} />
            </div>
          ) : activeCategory ? (
            <div className="items-section">
              <div className="section-header"><h2>{activeCategory.name}</h2></div>
              {activeCategory.items?.length === 0 ? (
                <div className="empty-state"><p>No items in this category</p></div>
              ) : (
                <div className="items-grid">
                  {activeCategory.items?.map((item) => (
                    <div
                      key={item.item_id}
                      className={`item-card ${isSimulator ? 'editable' : ''} ${item.featured ? 'is-featured' : ''}`}
                      onClick={() => openEditModal(item)}
                    >
                      {item.featured && <span className="featured-badge">★ Featured</span>}
                      {isSimulator && <span className="edit-indicator">✎</span>}
                      {item.image_url && (
                        <div className="item-image">
                          <img src={item.image_url} alt={item.name} />
                        </div>
                      )}
                      <div className="item-content">
                        <div className="item-header">
                          <h3>{item.name}</h3>
                          <span className="item-price">{formatPrice(item.price)}</span>
                        </div>
                        <p className="item-description">{item.description}</p>
                        {item.dietary && item.dietary.length > 0 && (
                          <div className="item-dietary">
                            {item.dietary.map((tagId) => {
                              const tag = DIETARY_TAGS.find(t => t.id === tagId);
                              return tag ? (
                                <span key={tagId} className="dietary-badge" style={{ backgroundColor: tag.color }}>
                                  {tag.label}
                                </span>
                              ) : null;
                            })}
                          </div>
                        )}
                        {item.modifiers && item.modifiers.length > 0 && (
                          <div className="item-modifiers-preview">
                            {item.modifiers.map((mod, i) => (
                              <span key={i} className="modifier-preview-tag">{mod.name}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state"><p>Select a category to view its items</p></div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content menu-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Menu Item</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {saveError && <div className="alert alert-error" style={{ marginBottom: 16 }}>{saveError}</div>}

              {/* Image */}
              <div className="edit-image-section">
                {editingItem.image ? (
                  <img src={editingItem.image} alt={editForm.name} className="edit-image-preview" />
                ) : (
                  <div className="edit-image-placeholder">No Image</div>
                )}
                <label className="btn btn-sm btn-secondary image-upload-btn">
                  {imageUploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} hidden disabled={imageUploading} />
                </label>
              </div>

              {/* Basic Fields */}
              <div className="edit-field">
                <label>Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="edit-field">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="edit-row">
                <div className="edit-field">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editForm.price}
                    onChange={(e) => setEditForm(f => ({ ...f, price: e.target.value }))}
                  />
                </div>
                <div className="edit-field">
                  <label>Calories</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.calories}
                    onChange={(e) => setEditForm(f => ({ ...f, calories: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="edit-field">
                <label>Ingredients</label>
                <textarea
                  value={editForm.ingredients}
                  onChange={(e) => setEditForm(f => ({ ...f, ingredients: e.target.value }))}
                  rows={2}
                  placeholder="Comma-separated: corn tortillas, carne asada, cilantro..."
                />
              </div>

              {/* Dietary Tags */}
              <div className="edit-field">
                <label>Dietary Restrictions</label>
                <div className="dietary-toggles">
                  {DIETARY_TAGS.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className={`dietary-toggle ${editForm.dietaryTags.includes(tag.id) ? 'active' : ''}`}
                      style={{
                        backgroundColor: editForm.dietaryTags.includes(tag.id) ? tag.color : 'transparent',
                        color: editForm.dietaryTags.includes(tag.id) ? 'white' : tag.color,
                        borderColor: tag.color,
                      }}
                      onClick={() => toggleDietaryTag(tag.id)}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Featured */}
              <div className="edit-field edit-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.featured}
                    onChange={(e) => setEditForm(f => ({ ...f, featured: e.target.checked }))}
                  />
                  Featured Item
                </label>
              </div>

              {/* Modifier Groups */}
              <div className="edit-field">
                <label>Modifier Groups</label>
                <div className="modifier-groups-list">
                  {editForm.optionGroups.length === 0 && (
                    <p className="no-modifiers">No modifier groups attached</p>
                  )}
                  {editForm.optionGroups.map((og) => {
                    const guid = typeof og === 'string' ? og : og.guid;
                    const options = getModifierOptions(og);
                    return (
                      <div key={guid} className="modifier-group-card">
                        <div className="modifier-group-header">
                          <span className="modifier-group-name">{getModifierGroupName(og)}</span>
                          <button
                            type="button"
                            className="modifier-remove-btn"
                            onClick={() => removeModifierGroup(guid)}
                          >×</button>
                        </div>
                        {options.length > 0 && (
                          <div className="modifier-options">
                            {options.map(pm => (
                              <span key={pm.guid} className="modifier-option">
                                {pm.name}
                                {pm.price > 0 && <span className="modifier-price">+${Number(pm.price).toFixed(2)}</span>}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {availableGroups.length > 0 && (
                    <select
                      className="modifier-add-select"
                      value=""
                      onChange={(e) => { if (e.target.value) addModifierGroup(e.target.value); }}
                    >
                      <option value="">+ Add modifier group...</option>
                      {availableGroups.map(og => (
                        <option key={og.guid} value={og.guid}>{og.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Menu;
